
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";

interface FreemiumLimits {
  projectCount: number;
  clientCount: number;
  planType: 'premium'; // Always premium now since everything is free
  canCreateProject: boolean;
  canCreateClient: boolean;
  projectLimitReached: boolean;
  clientLimitReached: boolean;
}

export function useFreemiumLimits() {
  const { session } = useAuth();

  return useQuery({
    queryKey: ['freemium-limits', session?.user?.id],
    queryFn: async (): Promise<FreemiumLimits> => {
      if (!session?.user) {
        throw new Error('Not authenticated');
      }

      // Get actual counts for display purposes
      const { count: projectCount } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', session.user.id)
        .eq('is_archived', false);

      // For now, we'll just set clientCount to 0 since everything is free
      const clientCount = 0;

      // Everything is now unlimited and free
      return {
        projectCount: projectCount || 0,
        clientCount: clientCount || 0,
        planType: 'premium', // Everyone gets premium access
        canCreateProject: true, // Always true
        canCreateClient: true, // Always true
        projectLimitReached: false, // Never reached
        clientLimitReached: false, // Never reached
      };
    },
    enabled: !!session?.user,
  });
}
